import gql from "graphql-tag";
import type { ApolloClient } from "@apollo/client";

import type {
  CancelTransactionMutation,
  CancelTransactionMutationVariables,
  GetAllTransactionsQuery,
  GetAllTransactionsQueryVariables,
  GetCashflowTransactionsQuery,
  GetCashflowTransactionsQueryVariables,
  GetTradeTransactionsQuery,
  GetTradeTransactionsQueryVariables,
} from "types/graphql";

export function getAllTransactions(
  client: ApolloClient<object>,
  accountId: string
): Promise<GetAllTransactionsQuery["getAllTransactions"]> {
  return new Promise((resolve, reject) => {
    const TRANSACTIONS = gql`
      query getAllTransactions($accountId: ID!) {
        getAllTransactions(accountId: $accountId) {
          memo
          status
          updatedAt
          id
          createdAt
          category
          cancellationStatus
          subCategory
          taxYear
        }
      }
    `;
    const result = client.query<
      GetAllTransactionsQuery,
      GetAllTransactionsQueryVariables
    >({
      query: TRANSACTIONS,
      variables: { accountId },
      // making a contribution creates a new transaction on the backend but if you request
      // transactions again from client it returns cached results. For now we wont cache until
      // can solve the cache issue
      fetchPolicy: "network-only",
    });
    result.then(
      (data) => {
        resolve(data.data.getAllTransactions);
      },
      (errors: Error) => {
        reject(errors);
      }
    );
  });
}
export function getCashflowTransactions(
  client: ApolloClient<object>,
  accountId: string
): Promise<GetCashflowTransactionsQuery["getCashflowTransactions"]> {
  return new Promise((resolve, reject) => {
    const CASHFLOW_TRANSACTIONS = gql`
      query getCashflowTransactions($accountId: ID!) {
        getCashflowTransactions(accountId: $accountId) {
          memo
          status
          updatedAt
          createdAt
        }
      }
    `;
    const result = client.query<
      GetCashflowTransactionsQuery,
      GetCashflowTransactionsQueryVariables
    >({
      query: CASHFLOW_TRANSACTIONS,
      variables: { accountId },
    });
    result.then(
      (data) => {
        resolve(data.data.getCashflowTransactions);
      },
      (errors: Error) => {
        reject(errors);
      }
    );
  });
}
export function getTradeTransactions(
  client: ApolloClient<object>,
  accountId: string
): Promise<GetTradeTransactionsQuery["getTradeTransactions"]> {
  return new Promise((resolve, reject) => {
    const TRADE_TRANSACTIONS = gql`
      query getTradeTransactions($accountId: ID!) {
        getTradeTransactions(accountId: $accountId) {
          memo
          status
          updatedAt
          createdAt
        }
      }
    `;
    const result = client.query<
      GetTradeTransactionsQuery,
      GetTradeTransactionsQueryVariables
    >({
      query: TRADE_TRANSACTIONS,
      variables: { accountId },
    });
    result.then(
      (data) => {
        resolve(data.data.getTradeTransactions);
      },
      (errors: Error) => {
        reject(errors);
      }
    );
  });
}

export function cancelTransaction(
  client: ApolloClient<object>,
  accountId: string,
  transactionId: string
): Promise<CancelTransactionMutation["cancelTransaction"]> {
  return new Promise((resolve, reject) => {
    const CANCEL_TRANSACTION = gql`
      mutation cancelTransaction($accountId: ID!, $transactionId: ID!) {
        cancelTransaction(accountId: $accountId, transactionId: $transactionId)
      }
    `;
    const result = client.mutate<
      CancelTransactionMutation,
      CancelTransactionMutationVariables
    >({
      mutation: CANCEL_TRANSACTION,
      variables: { accountId, transactionId },
    });
    result.then(
      (data) => {
        resolve(data.data?.cancelTransaction ?? null);
      },
      (errors: Error) => {
        reject(errors);
      }
    );
  });
}

export default {
  getAllTransactions,
  getCashflowTransactions,
  cancelTransaction,
  getTradeTransactions,
};
